import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { FindPeoplePlanState } from "@/pages/Private/redux/list/list-find-person.slice";

import { FindPeopleCostBox } from "./FindPeopleCostSections";
import {
	clampPeoplePerCompany,
	MAX_PEOPLE_PER_COMPANY,
	MIN_PEOPLE_PER_COMPANY,
} from "./findPeopleDialog.util";
import { SkippedRowsList } from "./FindPeopleResult";

const PREFIX = "defineIcps.bulkFindPeople";

interface PlanSectionProps {
	plan: FindPeoplePlanState;
	retryPlan: () => void;
	peoplePerCompany: number;
}

/**
 * The plan's part of the dialog: checking, the server's quote for the chosen people per company
 * and the rows left out, or why it failed (PREC-4398).
 */
export const FindPeoplePlanSection: FunctionComponent<PlanSectionProps> = ({
	plan,
	retryPlan,
	peoplePerCompany,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`${PREFIX}.${key}`);
	const quote =
		plan.status === "ready"
			? plan.plan.quotes.find((candidate) => candidate.peoplePerCompany === peoplePerCompany)
			: undefined;

	if (plan.status === "loading") {
		return (
			<div className="flex items-center gap-2 text-ssm text-text-meta" data-testid="plan-loading">
				<Icon className="h-4 w-4 animate-spin text-brand-light" icon={faSpinner} />
				{ts("planning")}
			</div>
		);
	}

	if (plan.status === "error" || !quote) {
		return (
			<div className="flex items-center justify-between gap-4" role="alert">
				<p className="text-ssm font-semibold text-status-error">{ts("planError")}</p>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.XS}
					title={ts("tryAgain")}
					type="button"
					onClick={retryPlan}
				/>
			</div>
		);
	}

	const { companies, balance, balanceCoversSearch, preflightCredits } = plan.plan;

	return (
		<>
			{companies > 0 ? (
				<FindPeopleCostBox
					balance={balance}
					companies={companies}
					quote={quote}
					shortfall={balanceCoversSearch ? null : { preflightCredits }}
				/>
			) : (
				<p className="text-ssm font-semibold text-status-error" role="alert">
					{ts("noCompanies")}
				</p>
			)}
			<SkippedRowsList
				headingKey="leftOut"
				ignoredRows={plan.plan.ignoredRows}
				rows={plan.plan.skippedRows}
			/>
		</>
	);
};

interface PeoplePerCompanyStepperProps {
	value: number;
	onChange: (value: number) => void;
}

/** People per company, within the shared bounds, as the Find person step's stepper. */
export const PeoplePerCompanyStepper: FunctionComponent<PeoplePerCompanyStepperProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();
	const stepClassName =
		"flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-border-light bg-white text-base text-gray-700 transition-colors hover:border-brand-light hover:text-brand-light disabled:cursor-not-allowed disabled:opacity-50";

	return (
		<section className="flex items-center justify-between gap-4 rounded-[10px] border border-border-light px-4 py-3.5">
			<div>
				<div className="text-sm font-bold">{t("defineIcps.peoplePerCompany")}</div>
				<div className="mt-0.5 text-xs text-text-meta">
					{t("defineIcps.peoplePerCompanySubtitle")}
				</div>
			</div>
			<div className="flex items-center gap-3.5">
				<button
					aria-label={t(`${PREFIX}.fewer`)}
					className={stepClassName}
					disabled={value <= MIN_PEOPLE_PER_COMPANY}
					type="button"
					onClick={() => onChange(clampPeoplePerCompany(value - 1))}
				>
					−
				</button>
				<span className="font-bold" data-testid="people-per-company">
					{value}
				</span>
				<button
					aria-label={t(`${PREFIX}.more`)}
					className={stepClassName}
					disabled={value >= MAX_PEOPLE_PER_COMPANY}
					type="button"
					onClick={() => onChange(clampPeoplePerCompany(value + 1))}
				>
					+
				</button>
			</div>
		</section>
	);
};
